"use client";

import { ChevronDown, Clock } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";

// Utility function for class merging
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(" ");
}

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

type TimePeriod = "AM" | "PM";

interface TimeValue {
  hours: number;
  minutes: number;
  seconds?: number;
  period?: TimePeriod;
}

interface TimePickerProps {
  value?: string;
  onChange?: (value: string) => void;
  format?: "12h" | "24h";
  showSeconds?: boolean;
  minuteStep?: number | number[];
  hourStep?: number;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  portalContainer?: HTMLElement | null;
  minTime?: string;
  maxTime?: string;
}

interface TimePickerItemProps {
  value: number | string;
  selected?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onClick?: () => void;
  className?: string;
  "data-hour"?: number;
  "data-minute"?: number;
}

// ============================================================================
// TIME PICKER ITEM COMPONENT
// ============================================================================

function TimePickerItem({
  value,
  selected = false,
  disabled = false,
  readOnly = false,
  onClick,
  className,
  "data-hour": dataHour,
  "data-minute": dataMinute
}: Readonly<TimePickerItemProps>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || readOnly}
      className={cn(
        "w-full px-3 py-2 text-sm rounded-md transition-all duration-200",
        "hover:bg-accent hover:text-accent-foreground",
        "disabled:pointer-events-none disabled:opacity-50",
        selected &&
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
        selected && readOnly && "bg-muted text-muted-foreground",
        readOnly && !selected && "bg-muted/50 text-muted-foreground",
        className
      )}
      data-slot="time-picker-item"
      data-hour={dataHour}
      data-minute={dataMinute}
    >
      {typeof value === "number" ? value.toString().padStart(2, "0") : value}
    </button>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Converts a 12-hour format hour to 24-hour format
 */
function convertTo24Hour(hour: number, period: TimePeriod | undefined): number {
  if (period === "PM" && hour !== 12) {
    return hour + 12;
  }
  if (period === "AM" && hour === 12) {
    return 0;
  }
  return hour;
}

/**
 * Checks if a given hour is disabled based on minTime constraints
 */
function isHourDisabledByMinTime(
  hour: number,
  format: "12h" | "24h",
  minTimeValue: TimeValue | null,
  currentPeriod: TimePeriod | undefined
): boolean {
  if (!minTimeValue) return false;

  if (format === "24h") {
    return hour < minTimeValue.hours;
  }

  // For 12h format, need to consider period
  const minHour24 = convertTo24Hour(minTimeValue.hours, minTimeValue.period);
  const currentHour24 = convertTo24Hour(hour, currentPeriod);
  return currentHour24 < minHour24;
}

/**
 * Checks if a given minute is disabled based on minTime constraints
 */
function isMinuteDisabledByMinTime(
  minute: number,
  currentHours: number,
  format: "12h" | "24h",
  minTimeValue: TimeValue | null,
  currentPeriod: TimePeriod | undefined
): boolean {
  if (!minTimeValue) return false;

  if (format === "24h") {
    if (currentHours < minTimeValue.hours) {
      return true;
    }
    if (currentHours === minTimeValue.hours && minute < minTimeValue.minutes) {
      return true;
    }
    return false;
  }

  // For 12h format, need to consider period
  const minHour24 = convertTo24Hour(minTimeValue.hours, minTimeValue.period);
  const currentHour24 = convertTo24Hour(currentHours, currentPeriod);

  if (currentHour24 < minHour24) {
    return true;
  }
  if (currentHour24 === minHour24 && minute < minTimeValue.minutes) {
    return true;
  }
  return false;
}

function parseTimeString(
  timeStr: string,
  format: "12h" | "24h"
): TimeValue | null {
  if (!timeStr) return null;

  const time12Regex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i;
  const time24Regex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

  let match;
  if (format === "12h") {
    match = time12Regex.exec(timeStr);
    if (match) {
      return {
        hours: parseInt(match[1]),
        minutes: parseInt(match[2]),
        seconds: match[3] ? parseInt(match[3]) : undefined,
        period: match[4].toUpperCase() as TimePeriod
      };
    }
  } else {
    match = time24Regex.exec(timeStr);
    if (match) {
      return {
        hours: parseInt(match[1]),
        minutes: parseInt(match[2]),
        seconds: match[3] ? parseInt(match[3]) : undefined
      };
    }
  }

  return null;
}

function formatTimeValue(
  timeValue: TimeValue,
  format: "12h" | "24h",
  showSeconds: boolean
): string {
  const { hours, minutes, seconds, period } = timeValue;

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds =
    showSeconds && seconds !== undefined
      ? `:${seconds.toString().padStart(2, "0")}`
      : "";

  if (format === "12h") {
    return `${formattedHours}:${formattedMinutes}${formattedSeconds} ${period}`;
  }

  return `${formattedHours}:${formattedMinutes}${formattedSeconds}`;
}

function parseTimeConstraint(
  timeStr: string | undefined
): { hours: number; minutes: number } | null {
  if (!timeStr) return null;

  const time24Regex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
  const match = time24Regex.exec(timeStr);

  if (match) {
    return {
      hours: parseInt(match[1]),
      minutes: parseInt(match[2])
    };
  }

  return null;
}

// ============================================================================
// MAIN TIME PICKER COMPONENT
// ============================================================================

function TimePicker({
  value = "",
  onChange,
  format = "12h",
  showSeconds = false,
  minuteStep = 1,
  hourStep = 1,
  placeholder = "Select time",
  disabled = false,
  readOnly = false,
  className,
  portalContainer,
  minTime,
  maxTime
}: Readonly<TimePickerProps>) {
  const [open, setOpen] = React.useState(false);
  const justOpened = React.useRef(false);
  const hourScrollRef = React.useRef<HTMLDivElement>(null);
  const minuteScrollRef = React.useRef<HTMLDivElement>(null);
  const [timeValue, setTimeValue] = React.useState<TimeValue>({
    hours: format === "12h" ? 12 : 0,
    minutes: 0,
    seconds: showSeconds ? 0 : undefined,
    period: format === "12h" ? "AM" : undefined
  });
  const [hasChanges, setHasChanges] = React.useState(false);
  const [originalValue, setOriginalValue] = React.useState<TimeValue | null>(
    null
  );

  // Parse minTime if provided
  const minTimeValue = React.useMemo(() => {
    if (!minTime) return null;
    return parseTimeString(minTime, format);
  }, [minTime, format]);

  // Parse initial value
  React.useEffect(() => {
    if (value) {
      const parsed = parseTimeString(value, format);
      if (parsed) {
        setTimeValue(parsed);
        setOriginalValue(parsed);
      }
    } else if (minTimeValue) {
      // Set default values when no initial value - use minTime if provided
      setTimeValue(minTimeValue);
      setOriginalValue(minTimeValue);
    } else {
      // Set default values when no initial value and no minTime
      const defaultValue: TimeValue = {
        hours: format === "12h" ? 12 : 0,
        minutes: 0,
        seconds: showSeconds ? 0 : undefined,
        period: format === "12h" ? ("AM" as "AM" | "PM") : undefined
      };
      setTimeValue(defaultValue);
      setOriginalValue(defaultValue);
    }
  }, [value, format, showSeconds, minTimeValue]);

  React.useEffect(() => {
    if (open) {
      justOpened.current = true;
      requestAnimationFrame(() => {
        justOpened.current = false;
      });

      // Scroll to selected hour and minute when picker opens
      setTimeout(() => {
        if (hourScrollRef.current) {
          const selectedHourElement = hourScrollRef.current.querySelector(
            `[data-hour="${timeValue.hours}"]`
          );
          if (selectedHourElement) {
            selectedHourElement.scrollIntoView({
              block: "center",
              behavior: "smooth"
            });
          }
        }
        if (minuteScrollRef.current) {
          const selectedMinuteElement = minuteScrollRef.current.querySelector(
            `[data-minute="${timeValue.minutes}"]`
          );
          if (selectedMinuteElement) {
            selectedMinuteElement.scrollIntoView({
              block: "center",
              behavior: "smooth"
            });
          }
        }
      }, 100);
    }
  }, [open, timeValue.hours, timeValue.minutes]);

  // Parse time constraints
  const minTimeConstraint = React.useMemo(
    () => parseTimeConstraint(minTime),
    [minTime]
  );
  const maxTimeConstraint = React.useMemo(
    () => parseTimeConstraint(maxTime),
    [maxTime]
  );

  // Generate time options
  const hourOptions = React.useMemo(() => {
    const max = format === "12h" ? 12 : 23;
    const min = format === "12h" ? 1 : 0;
    const options = [];
    for (let i = min; i <= max; i += hourStep) {
      // Filter hours based on minTime/maxTime constraints (24h format only)
      if (format === "24h") {
        if (minTimeConstraint && i < minTimeConstraint.hours) continue;
        if (maxTimeConstraint && i > maxTimeConstraint.hours) continue;
      }
      options.push(i);
    }
    return options;
  }, [format, hourStep, minTimeConstraint, maxTimeConstraint]);

  const minuteOptions = React.useMemo(() => {
    if (Array.isArray(minuteStep)) {
      return minuteStep;
    }
    const options = [];
    for (let i = 0; i < 60; i += minuteStep) {
      // Filter minutes based on minTime/maxTime constraints when at boundary hours (24h format only)
      if (format === "24h") {
        if (
          minTimeConstraint &&
          timeValue.hours === minTimeConstraint.hours &&
          i < minTimeConstraint.minutes
        ) {
          continue;
        }
        if (
          maxTimeConstraint &&
          timeValue.hours === maxTimeConstraint.hours &&
          i > maxTimeConstraint.minutes
        ) {
          continue;
        }
      }
      options.push(i);
    }
    return options;
  }, [
    minuteStep,
    format,
    minTimeConstraint,
    maxTimeConstraint,
    timeValue.hours
  ]);

  const secondOptions = React.useMemo(() => {
    if (!showSeconds) return [];
    const options = [];
    for (let i = 0; i < 60; i++) {
      options.push(i);
    }
    return options;
  }, [showSeconds]);

  // Helper function to get valid minute options for a given hour
  const getValidMinutesForHour = React.useCallback(
    (hour: number): number[] => {
      if (Array.isArray(minuteStep)) {
        return minuteStep;
      }
      const options: number[] = [];
      for (let i = 0; i < 60; i += minuteStep) {
        if (format === "24h") {
          if (
            minTimeConstraint &&
            hour === minTimeConstraint.hours &&
            i < minTimeConstraint.minutes
          ) {
            continue;
          }
          if (
            maxTimeConstraint &&
            hour === maxTimeConstraint.hours &&
            i > maxTimeConstraint.minutes
          ) {
            continue;
          }
        }
        options.push(i);
      }
      return options;
    },
    [minuteStep, format, minTimeConstraint, maxTimeConstraint]
  );

  const handleTimeChange = (
    field: keyof TimeValue,
    newValue: number | string
  ) => {
    const updated = { ...timeValue, [field]: newValue };

    // When hour changes, reset minutes to the first available minute for that hour
    if (field === "hours" && typeof newValue === "number") {
      const validMinutes = getValidMinutesForHour(newValue);
      if (validMinutes.length > 0) {
        // Always reset to first available minute when hour changes
        updated.minutes = validMinutes[0];
      }
    }

    setTimeValue(updated);
    setHasChanges(true);
  };

  const handleConfirm = () => {
    const timeString = formatTimeValue(timeValue, format, showSeconds);
    onChange?.(timeString);
    setOpen(false);
    setHasChanges(false);
    setOriginalValue({ ...timeValue });
  };

  const handleCancel = () => {
    // Reset to original value
    if (originalValue) {
      setTimeValue({ ...originalValue });
    }
    setOpen(false);
    setHasChanges(false);
  };

  const displayValue = value || placeholder;

  // Helper for grid columns
  const gridColsClass = React.useMemo(() => {
    if (showSeconds && format === "12h") {
      return "grid-cols-4";
    }
    if (showSeconds || format === "12h") {
      return "grid-cols-3";
    }
    return "grid-cols-2";
  }, [showSeconds, format]);

  // Read-only mode: just show the input without popover functionality
  if (readOnly) {
    return (
      <Button
        variant="outline"
        className={cn(
          "w-full justify-start text-left font-normal cursor-default bg-muted/50",
          !value && "text-muted-foreground",
          className
        )}
        disabled
        data-slot="time-picker-trigger"
      >
        <Clock className="h-4 w-4" />
        {displayValue}
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal border border-[hsl(var(--border))] border-solid flex gap-4 flex-row content-center justify-center items-center",
            !value && "text-muted-foreground",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
          disabled={disabled}
          data-slot="time-picker-trigger"
        >
          <Clock className="h-4 w-4" />
          {displayValue}
          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto max-w-[320px] p-0 time-picker-popover radix-motion-none data-[state=open]:animate-none"
        align="start"
        side="bottom"
        sideOffset={4}
        container={portalContainer}
        noMotion={true}
        onPointerDownOutside={(e) => {
          if (justOpened.current) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (justOpened.current) {
            e.preventDefault();
          }
        }}
      >
        <div className="p-2 space-y-4" data-slot="time-picker-content">
          {/* Visual interface with proper select elements for SonarQube compliance */}
          <div className={cn("grid gap-1", gridColsClass)}>
            {/* Hours Column */}
            <div className="space-y-2">
              <label
                htmlFor="time-picker-hours"
                className="text-sm font-medium text-center block"
              >
                Hours
              </label>
              <select
                id="time-picker-hours"
                className="sr-only"
                multiple
                size={10}
                value={[timeValue.hours.toString()]}
                onChange={(e) => {
                  const selectedValue = parseInt(e.target.value);
                  handleTimeChange("hours", selectedValue);
                }}
                disabled={disabled}
                aria-label="Select hours"
              >
                {hourOptions.map((hour) => {
                  const isDisabledByMin = isHourDisabledByMinTime(
                    hour,
                    format,
                    minTimeValue,
                    timeValue.period
                  );
                  return (
                    <option key={hour} value={hour} disabled={isDisabledByMin}>
                      {hour.toString().padStart(2, "0")}
                    </option>
                  );
                })}
              </select>
              <div
                ref={hourScrollRef}
                className="h-48 overflow-y-auto space-y-1 p-0.5 bg-background"
              >
                {hourOptions.map((hour) => {
                  const isDisabledByMinTime = isHourDisabledByMinTime(
                    hour,
                    format,
                    minTimeValue,
                    timeValue.period
                  );
                  const isDisabled = disabled || isDisabledByMinTime;

                  return (
                    <TimePickerItem
                      key={hour}
                      value={hour}
                      selected={timeValue.hours === hour}
                      onClick={() => handleTimeChange("hours", hour)}
                      disabled={isDisabled}
                      readOnly={false}
                      data-hour={hour}
                    />
                  );
                })}
              </div>
            </div>

            {/* Minutes Column */}
            <div className="space-y-2">
              <label
                htmlFor="time-picker-minutes"
                className="text-sm font-medium text-center block"
              >
                Minutes
              </label>
              <select
                id="time-picker-minutes"
                className="sr-only"
                multiple
                size={10}
                value={[timeValue.minutes.toString()]}
                onChange={(e) => {
                  const selectedValue = parseInt(e.target.value);
                  handleTimeChange("minutes", selectedValue);
                }}
                disabled={disabled}
                aria-label="Select minutes"
              >
                {minuteOptions.map((minute) => {
                  const isDisabledByMin = isMinuteDisabledByMinTime(
                    minute,
                    timeValue.hours,
                    format,
                    minTimeValue,
                    timeValue.period
                  );
                  return (
                    <option
                      key={minute}
                      value={minute}
                      disabled={isDisabledByMin}
                    >
                      {minute.toString().padStart(2, "0")}
                    </option>
                  );
                })}
              </select>
              <div
                ref={minuteScrollRef}
                className="h-48 overflow-y-auto space-y-1 p-0.5 bg-background"
              >
                {minuteOptions.map((minute) => {
                  const isDisabledByMinTime = isMinuteDisabledByMinTime(
                    minute,
                    timeValue.hours,
                    format,
                    minTimeValue,
                    timeValue.period
                  );
                  const isDisabled = disabled || isDisabledByMinTime;

                  return (
                    <TimePickerItem
                      key={minute}
                      value={minute}
                      selected={timeValue.minutes === minute}
                      onClick={() => handleTimeChange("minutes", minute)}
                      disabled={isDisabled}
                      readOnly={false}
                      data-minute={minute}
                    />
                  );
                })}
              </div>
            </div>

            {/* Seconds Column */}
            {showSeconds && (
              <div className="space-y-2">
                <label
                  htmlFor="time-picker-seconds"
                  className="text-sm font-medium text-center block"
                >
                  Seconds
                </label>
                <select
                  id="time-picker-seconds"
                  className="sr-only"
                  multiple
                  size={10}
                  value={[timeValue.seconds?.toString() ?? "0"]}
                  onChange={(e) => {
                    const selectedValue = parseInt(e.target.value);
                    handleTimeChange("seconds", selectedValue);
                  }}
                  disabled={disabled}
                  aria-label="Select seconds"
                >
                  {secondOptions.map((second) => (
                    <option key={second} value={second}>
                      {second.toString().padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <div className="h-48 overflow-y-auto space-y-1 p-0.5 bg-background">
                  {secondOptions.map((second) => (
                    <TimePickerItem
                      key={second}
                      value={second}
                      selected={timeValue.seconds === second}
                      onClick={() => handleTimeChange("seconds", second)}
                      disabled={disabled}
                      readOnly={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* AM/PM Column */}
            {format === "12h" && (
              <div className="space-y-2">
                <label
                  htmlFor="time-picker-period"
                  className="text-sm font-medium text-center block"
                >
                  Period
                </label>
                <select
                  id="time-picker-period"
                  className="sr-only"
                  multiple
                  size={2}
                  value={[timeValue.period ?? "AM"]}
                  onChange={(e) => {
                    handleTimeChange("period", e.target.value as TimePeriod);
                  }}
                  disabled={disabled}
                  aria-label="Select period"
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
                <div className="h-48 flex flex-col space-y-1 p-0.5 bg-background">
                  <TimePickerItem
                    value="AM"
                    selected={timeValue.period === "AM"}
                    onClick={() => handleTimeChange("period", "AM")}
                    disabled={disabled}
                    readOnly={false}
                  />
                  <TimePickerItem
                    value="PM"
                    selected={timeValue.period === "PM"}
                    onClick={() => handleTimeChange("period", "PM")}
                    disabled={disabled}
                    readOnly={false}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={disabled}
              data-slot="time-picker-cancel"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={disabled || !hasChanges}
              data-slot="time-picker-confirm"
            >
              Confirm
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { TimePicker, TimePickerItem };
export type { TimePickerItemProps, TimePickerProps, TimeValue };
